import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { MaskedLines, FadeUp, SectionTag } from "@/components/Reveal";
import { PRICING, PRICING_INCLUDED, PRICING_EXTRA, mailto } from "@/data/content";

const Kosten = () => (
  <div data-testid="kosten-page">
    <SEO
      title="Kosten – Preise auf Anfrage für Ihre Zeremonie | Daviz & Puxi"
      description="Was kostet eine individuelle Zeremonie? Transparente Leistungspakete für Trauungen, Willkommensfeiern und Jubiläen – persönliches Festpreis-Angebot auf Anfrage."
      path="/kosten"
    />

    <section className="mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
      <FadeUp>
        <SectionTag>Kosten</SectionTag>
      </FadeUp>
      <MaskedLines
        lines={["Klare Worte,", "auch beim Preis."]}
        className="mt-6 font-serif text-5xl leading-[1.05] sm:text-7xl"
        delay={0.15}
      />
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl text-lg leading-relaxed text-stone">
          Sie sollen genau wissen, was Sie bekommen – und was es kostet. Unsere Preise verstehen
          sich als Richtwerte; Ihr persönliches Angebot erstellen wir nach dem ersten Gespräch.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <div className="grid gap-6 md:grid-cols-2 lg:mx-auto lg:max-w-5xl">
        {PRICING.map((p, i) => (
          <FadeUp key={p.name} delay={i * 0.1}>
            <div
              data-testid={`pricing-${i}`}
              className={`flex h-full flex-col rounded-2xl border p-8 sm:p-10 ${
                p.highlighted
                  ? "border-moss bg-moss text-creme"
                  : "border-sand bg-parch/50 text-ink"
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-[0.25em] ${p.highlighted ? "text-sand" : "text-rust"}`}>
                {p.name}
              </p>
              <p className="mt-5 font-serif text-4xl sm:text-5xl">{p.price}</p>
              <p className={`mt-2 text-sm ${p.highlighted ? "text-creme/70" : "text-stone"}`}>{p.note}</p>
              <ul className={`mt-8 flex-1 space-y-3 border-t pt-7 text-sm ${p.highlighted ? "border-creme/20" : "border-sand"}`}>
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={16} className={`mt-0.5 shrink-0 ${p.highlighted ? "text-sand" : "text-moss"}`} />
                    <span className={p.highlighted ? "text-creme/90" : "text-stone"}>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={mailto(`Angebot: ${p.name}`)}
                data-testid={`pricing-cta-${i}`}
                className={`mt-9 inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-transform duration-300 hover:scale-[1.03] ${
                  p.highlighted ? "bg-creme text-mossdeep" : "bg-moss text-creme hover:bg-mossdeep"
                }`}
              >
                Persönliches Angebot anfragen <ArrowRight size={15} />
              </a>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp className="mt-8">
        <p className="text-center text-xs text-stone">
          Ihr persönliches Festpreis-Angebot erhalten Sie nach dem Kennenlerngespräch – kostenfrei, transparent und unverbindlich.
        </p>
      </FadeUp>
    </section>

    <section className="bg-parch py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <FadeUp>
          <SectionTag>Was ist im Preis enthalten?</SectionTag>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl">Immer dabei – ohne Aufpreis</h2>
          <ul className="mt-8 space-y-4">
            {PRICING_INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-3 border-b border-sand/70 pb-4 text-stone">
                <Check size={18} className="mt-0.5 shrink-0 text-moss" />
                <span className="leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.15}>
          <SectionTag>Mögliche Zusatzleistungen</SectionTag>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl">Nur, wenn Sie es wünschen</h2>
          <div className="mt-8 space-y-6">
            {PRICING_EXTRA.map((x) => (
              <div key={x.label} className="rounded-xl border border-sand bg-creme p-6">
                <p className="font-serif text-lg">{x.label}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-stone">{x.text}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>

    <CTASection
      title={["Ihr Angebot,", "so individuell wie Ihr Anlass."]}
      text="Jede Zeremonie ist anders – und manchmal auch der Preis. Schreiben Sie uns, wir erstellen Ihnen ein transparentes, persönliches Angebot."
      buttonLabel="Persönliches Angebot anfragen"
      subject="Persönliches Angebot anfragen"
    />
  </div>
);

export default Kosten;
